import {
  NgwResource,
  createNgwLayerAdapter,
  createOnFirstShowAdapter,
} from '@nextgis/ngw-kit';
import { applyMixins } from '@nextgis/utils';

import { quality } from '../../services/quality';

import { createGeoJsonAdapter as createGeoJsonAdapter_ } from './createStyle3DAdapter';
import { createTileset3DAdapter } from './createTileset3dAdapter';

import type { Scene3DTreeItem, Scene3DTreeItemTileset3D } from './interfaces';
import type NgwConnector from '@nextgis/ngw-connector';
import type { TreeItem } from '@nextgis/ngw-kit';
import type { LngLatBoundsArray, Type } from '@nextgis/utils';
import type { AdapterOptions, MainLayerAdapter, WebMap } from '@nextgis/webmap';

export async function getAdapterFromItemType(
  item: TreeItem,
  options: Record<string, any>,
  webMap: WebMap,
  connector?: NgwConnector,
): Promise<Type<MainLayerAdapter<any, any, AdapterOptions>> | undefined> {
  if (connector && item.item_type === 'item') {
    const item_ = item as Scene3DTreeItem;
    const createAdapter = async () => {
      const cls = item_.resource_cls;
      let resourceAdapter: Type<MainLayerAdapter> | undefined;
      if (cls === 'style_3d') {
        resourceAdapter = await getStyle3DAdapter(
          item_,
          options,
          webMap,
          connector,
        );
      } else if (cls === 'file_bucket' || cls === 'tileset_3d') {
        resourceAdapter = await getTileset3DAdapter(
          item_ as Scene3DTreeItemTileset3D,
          webMap,
          connector,
        );
      } else {
        resourceAdapter = await createNgwLayerAdapter(
          {
            resource: item_.resource_id,
            adapterOptions: { name: item_.display_name },
          },
          webMap,
          connector,
        );
      }
      if (resourceAdapter) {
        resourceAdapter.prototype.connector = connector;
        applyMixins(resourceAdapter, [NgwResource], { replace: false });
        return resourceAdapter;
      }
    };
    const FirstShowAdapter = await createOnFirstShowAdapter({
      createAdapter,
      webMap,
    });
    class FirstShow extends FirstShowAdapter {
      protected _extent?: LngLatBoundsArray;

      async getExtent(): Promise<LngLatBoundsArray | undefined> {
        const layers = await this.loadLayer();
        const getExtentLayer = layers.find((x) => x.getExtent);
        if (getExtentLayer && getExtentLayer.getExtent) {
          return getExtentLayer.getExtent();
        }
      }
    }
    return FirstShow;
  }
  return undefined;
}

async function getTileset3DAdapter(
  item: Scene3DTreeItemTileset3D,
  webMap: WebMap,
  connector: NgwConnector,
): Promise<Type<MainLayerAdapter> | undefined> {
  const res = await connector.getResource(item.resource_id);
  if (
    res &&
    // @ts-expect-error deprecated - backward compatibility
    (res.file_bucket || res.tileset_3d)
  ) {
    return await createTileset3DAdapter({
      webMap,
      connector,
      item: res,
      layerOptions: {
        resource: item.resource_id,
        adapterOptions: {
          nativeOptions: {
            maximumScreenSpaceError: quality.getMaximumScreenSpaceError(),
          },
          paint: {
            // 'rgba(255,255,255,0.5)'
            color: item.tileset_3d_color,
          },
        },
      },
    });
  }
}

async function getStyle3DAdapter(
  item: Scene3DTreeItem,
  options: any,
  webMap: WebMap,
  connector: NgwConnector,
): Promise<Type<MainLayerAdapter> | undefined> {
  let resourceAdapter: Type<MainLayerAdapter> | undefined;

  if (item.resource_cls === 'style_3d') {
    const vectorItem = await connector.getResource(item.parent);

    if (vectorItem && 'payload' in item) {
      options.visibility = item.visible;
      resourceAdapter = await createGeoJsonAdapter_(vectorItem, item.payload, {
        layerOptions: {
          resource: vectorItem.resource.id,
          adapterOptions: {
            popupOptions: options.popupOptions,
          },
        },
        connector,
        webMap,
        item: vectorItem,
      });
      if (resourceAdapter) {
        resourceAdapter.prototype.item = vectorItem;
      }
      return resourceAdapter;
    }
  }
}
